import React from "react"
import dayjs from "dayjs"
import { useDispatch, useSelector } from "react-redux"
import { ref, get } from "firebase/database"
import { useHistory } from "react-router-dom"
import { doEnforceHomePickupCredit } from "@coast/utils"

import { isLoaded } from "../../../actions/globalUiActions"
import {
  TransientSlot,
  SlotToPick,
  ProductPackageWithPrices,
  MeetingType,
  LessonTypeName,
} from "../../../types"
import { sdk9db } from "../../../firebase"
import { RootState } from "../../../store/configureStore"
import { DEFAULT_TIMEZONE, STATES_CONFIG } from "../../../config"
import makeSlug from "../../../utils/makeSlug"
import { logError } from "../../../utils/errors"
import { setTransientSlotId } from "../../../actions/lessonActions"
import {
  doMoreSearch,
  resetSearch,
  setDefaultLessonTypeForState,
  setSpotTaken,
  startNewSearch,
  stopSearch,
} from "../../../actions/searchActions"
import useSlots from "../checkout/useSlots"
import { usePackagesWithPrices } from "../../../hooks/usePackagesWithPrices"
import { useGeocodeFromPartialAddress } from "../../../hooks/useGeocodeFromPartialAddress"
import { filterPackages } from "../../../packages"

/**
 * This holds the state needed for doing searches, viewing slots,
 * seeing errors, etc. It's like an interface to the Redux actions.
 *
 * The name isn't necessarily right: usePicker could be useSearch
 * or useAvailSearch. We may change it. But for now, it just means
 * it's a custom hook for the AvailabilityPicker (which itself is
 * not a great name).
 */

// We'll stop searching when we get MIN_RESULT_DAYS of results, or
// when we've gone beyond MAX_DAYS_OUT, whichever comes first.
const MAX_DAYS_OUT = 60

export const usePicker = (instructorIdToSearch?: string) => {
  const {
    firebase: { profile },
    location: { searchAddress, searchCity, searchState, stateConfig },
    search: { outOfServiceArea, endDate, selectedLessonType, includePrivate },
  } = useSelector((state: RootState) => state)
  const dispatch = useDispatch()
  const history = useHistory()
  const { createTransientSlot } = useSlots()
  const geocodeHook = useGeocodeFromPartialAddress(searchAddress)
  const pricesHook = usePackagesWithPrices(
    geocodeHook.data?.state,
    geocodeHook.data,
  )

  const filteredPackages = React.useMemo(() => {
    if (!pricesHook.data?.packagesWithPrice) return null

    return filterPackages(
      pricesHook.data?.packagesWithPrice,
      selectedLessonType.name,
    )
  }, [pricesHook.data?.packagesWithPrice, selectedLessonType.name])

  React.useEffect(() => {
    // If we don't have package prices for the selected lesson type,
    // revert to the default type for that state.
    if (filteredPackages?.length === 0 && searchState) {
      dispatch(setDefaultLessonTypeForState(searchState))
    }
  }, [dispatch, filteredPackages?.length, searchState])

  React.useEffect(() => {
    if (stateConfig) {
      dispatch(stopSearch())
      dispatch(resetSearch())
      dispatch(
        startNewSearch(
          selectedLessonType.durationMinutes,
          instructorIdToSearch,
        ),
      )
    }
    // Note `includePrivate` is included as a dependency to kick off a new search when it changes.
  }, [
    dispatch,
    stateConfig,
    selectedLessonType.durationMinutes,
    instructorIdToSearch,
    includePrivate,
  ])

  const getPriceForTransient = (
    lessonTypeName: string,
    meetingPointId: string,
    packagesWithPrice: ProductPackageWithPrices[],
    state: string,
  ) => {
    // This mimics the packages split by lesson type we have in redux
    // we need to get the right price/msrp and therefore package
    // for a lesson being scheduled based on the lesson type and meeting point, etc.
    const isHomePickup = meetingPointId === MeetingType.Home
    // note: packagesWithPrice should already be filtered by state + lessonType
    return (
      packagesWithPrice
        .filter((pack) => {
          if (pack.hasCourse) return false // we only care about lesson packages
          if (
            pack.lessonPackage?.numRoadTests &&
            (lessonTypeName === LessonTypeName.DmvTest ||
              lessonTypeName.includes("road-test"))
          )
            return true // if road test we don't check home pickup
          if (doEnforceHomePickupCredit(state)) {
            // if enforcing home pickup check package matches meeting type.
            if (isHomePickup && !pack.isHomePickup) return false
            if (!isHomePickup && pack.isHomePickup) return false
          }

          return true
        })
        .sort((a, b) => a.price - b.price)[0]?.msrp || 0
    )
  }

  const isSpotAvailable = (
    startTime: number,
    endTime: number,
    instructorId: string,
    isShared: boolean,
  ) => {
    // Before we let someone start reserving a slot, we have to check our transient
    // slots to make sure someone else hasn't already clicked into it.
    // This collection is small so it's okay to pull the whole thing.
    const transProm = get(ref(sdk9db, "transientSlots")).then(
      (transientsSnap) => {
        let spotIsAvailable = true
        transientsSnap.forEach((transientSnap) => {
          const transient = transientSnap.val()
          if (
            transient.startTime <= endTime &&
            transient.endTime >= startTime &&
            transient.instructor.id === instructorId
          ) {
            spotIsAvailable = false
          }
        })
        return spotIsAvailable
      },
    )

    const lessonProm = isShared
      ? Promise.resolve(true)
      : get(ref(sdk9db, `lessonSlotDateTimes/${instructorId}`)).then(
          (lessonsSnap) => {
            let spotIsAvailable = true
            lessonsSnap.forEach((lessonSnap) => {
              const lesson = lessonSnap.val()
              if (lesson.startTime < endTime && lesson.endTime > startTime) {
                spotIsAvailable = false
              }
            })
            return spotIsAvailable
          },
        )

    return Promise.all([transProm, lessonProm]).then(
      ([isAvailableTrans, isAvailableLesson]) =>
        isAvailableTrans && isAvailableLesson,
    )
  }

  const reserveSpot = (selectedSlot: SlotToPick) => {
    const { startTime, endTime, instructorId, location, isUnpaired, lessonId } =
      selectedSlot

    get(ref(sdk9db, `instructors/${instructorId}`))
      .then((instructorSnap) => {
        const instructor = instructorSnap.val()
        if (!instructor) {
          throw new Error("No instructor found")
        }
        instructor.id = instructorSnap.key
        return instructor
      })
      .then((instructor) => {
        const packPrice = getPriceForTransient(
          selectedLessonType.name,
          location.meetingPointId,
          filteredPackages || [],
          instructor.state || "CA",
        )
        const slot: Omit<TransientSlot, "id"> = {
          instructor: {
            displayName:
              instructor.displayName ||
              `${instructor.firstName} ${instructor.lastName}`,
            id: instructor.id,
            portraitImage: instructor.portraitImage || "",
            wallpaperImage: instructor.wallpaperImage || "",
            userId: instructor.userId,
            about: instructor.about || "",
            slackUsername: instructor.slackUsername || "",
            timeZone: instructor.timeZone || DEFAULT_TIMEZONE,
            state: instructor.state || "",
            maxBaseTravelMinutes: instructor.maxBaseTravelMinutes || 0,
            maxBetweenTravelMinutes: instructor.maxBetweenTravelMinutes || 0,
          },
          startTime: startTime.valueOf(),
          endTime: endTime.valueOf(),
          locationId: makeSlug(searchCity),
          searchAddress: searchAddress || "",
          searchCity: searchCity || "",
          searchState: searchState || "",
          pickupAddress: location.address,
          price: packPrice,
          meetingPointId: location.meetingPointId,
          lessonType: {
            name: selectedLessonType.name,
            filterLabel: selectedLessonType.filterLabel ?? "",
            label: selectedLessonType.label,
            durationMinutes: selectedLessonType.durationMinutes,
          },
          updatedAt: new Date().valueOf(),
          isShareable:
            selectedLessonType.name === "shared" ||
            !!STATES_CONFIG[searchState || ""]?.isShareable, // hack for 6x6
          isShared: isUnpaired ?? false,
        }

        createTransientSlot(slot, lessonId).then((slotId) => {
          dispatch(setTransientSlotId(slotId))
          history.push(`/book-lesson/${slotId}/1`)
        })
      })
      .catch(logError)
  }

  const handleTimeSelected = (selectedSlot: SlotToPick) => {
    const {
      startTime,
      endTime,
      instructorId,
      isUnpaired = false,
    } = selectedSlot

    // Before we actually start booking, we have to make sure someone
    // else hasn't already clicked into this date/time.
    isSpotAvailable(startTime, endTime, instructorId, isUnpaired).then(
      (spotIsAvailable) => {
        if (spotIsAvailable) {
          reserveSpot(selectedSlot)
        } else {
          dispatch(setSpotTaken(true))
        }
      },
    )
  }

  const handleMoreClicked = () => {
    dispatch(
      doMoreSearch(selectedLessonType.durationMinutes, instructorIdToSearch),
    )
  }

  const isCityNotAvailable = outOfServiceArea && searchCity
  const isDoneLoadingBasics =
    isLoaded(profile) &&
    filteredPackages &&
    !!selectedLessonType &&
    !!searchCity

  const maxDaysOut = dayjs().add(MAX_DAYS_OUT, "days")
  const didSearchedBeyondMax = endDate > maxDaysOut

  return {
    handleTimeSelected,
    handleMoreClicked,
    isCityNotAvailable,
    isDoneLoadingBasics,
    maxDaysOut,
    didSearchedBeyondMax,
    filteredPackages,
  }
}
